import React from 'react';
import { Box, Typography, Chip } from '@material-ui/core';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CancelIcon from '@material-ui/icons/Cancel';
import { renderField, renderSelectedDataElement, renderListView } from '../fieldRenderers';
import { renderConditionsTable, renderLayoutTable, renderExternalFilters, renderSplitPane, renderBlocksTable } from '../tableRenderers';
import { renderSteps, renderHeaderTableConfig, renderTabsConfig, renderSelectedTopLevelDataElement, renderItemsObject } from '../specialRenderers';
import { SummaryItemsRenderer } from './SummaryItemsRenderer';
import { SelectedInnerDataElementRenderer } from './SelectedInnerDataElementRenderer';

/**
 * Centralized field router that handles all special field types
 * Eliminates duplicate code across different renderers
 */
export const FieldRouter = ({
	fieldKey,
	value,
	classes,
	showLabel = true,
	variant = 'default' // 'default', 'compact', 'inline'
}) => {
	// Handle arrays
	if (Array.isArray(value)) {
		if (fieldKey === 'selectedDataElement') {
			return renderSelectedDataElement(value, classes);
		}
		if (fieldKey === 'selectedTopLevelDataElement') {
			return renderSelectedTopLevelDataElement(value, classes);
		}
		if (fieldKey === 'selectedInnerDataElement') {
			return (
				<SelectedInnerDataElementRenderer
					selectedInnerDataElement={value}
					classes={classes}
					asChips={true}
					showLabel={showLabel}
				/>
			);
		}
		if (fieldKey === 'conditions') {
			return renderConditionsTable(value);
		}
		if (fieldKey === 'externalFilters') {
			return renderExternalFilters(value);
		}
		if (fieldKey === 'blocks') {
			return renderBlocksTable(value, classes);
		}
		if (fieldKey === 'steps') {
			return renderSteps(value, classes);
		}
	}

	// Handle objects
	if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
		if (fieldKey === 'listView') {
			return renderListView(value, classes, renderSelectedDataElement);
		}
		if (fieldKey === 'layout') {
			return renderLayoutTable(value);
		}
		if (fieldKey === 'splitPane') {
			return renderSplitPane(value);
		}
		if (fieldKey === 'headerTableConfig') {
			return renderHeaderTableConfig(value, classes);
		}
		if (fieldKey === 'headerTableConditionConfig') {
			return renderItemsObject(value, classes);
		}
		if (fieldKey === 'tabsConfig') {
			return renderTabsConfig(value, classes);
		}
		if (fieldKey === 'lineItems' || fieldKey === 'poLineItems') {
			return renderItemsObject(value, classes);
		}
		if (fieldKey === 'summaryItems') {
			return (
				<SummaryItemsRenderer
					summaryItems={value}
					classes={classes}
					showLabel={showLabel}
				/>
			);
		}
		if (fieldKey === 'compareLineItemsAndPOInfo') {
			return renderItemsObject(value, classes);
		}
		if (fieldKey === 'autoRefresh') {
			return renderItemsObject(value, classes);
		}
	}

	// Default: use renderField for unhandled cases, but without label if showLabel is false
	if (showLabel === false) {
		// Return just the value part without the label
		if (value === null || value === undefined) {
			return null;
		}
		if (typeof value === 'boolean') {
			return value === true ? (
				<CheckCircleIcon style={{ color: '#4caf50', marginLeft: 4 }} fontSize="small" />
			) : value === false ? (
				<CancelIcon style={{ color: '#f44336', marginLeft: 4 }} fontSize="small" />
			) : null;
		}
		if (Array.isArray(value)) {
			return value.length === 0 ? (
				<Typography component="span" color="textSecondary">(Empty array)</Typography>
			) : (
				<Chip label={`Array (${value.length} items)`} className={classes?.chip} size="small" />
			);
		}
		if (typeof value === 'object') {
			return <Chip label={`Object (${Object.keys(value).length} keys)`} className={classes?.chip} size="small" />;
		}
		return <Typography component="span">{String(value)}</Typography>;
	}
	return renderField(fieldKey, value, classes);
};

/**
 * Render field with label wrapper
 */
export const renderFieldWithLabel = (key, value, classes, options = {}) => {
	const {
		showLabel = true,
		label = key,
		variant = 'default', // 'default', 'compact', 'inline'
		mb = 2
	} = options;

	const fieldContent = <FieldRouter fieldKey={key} value={value} classes={classes} showLabel={false} variant={variant} />;

	if (variant === 'inline') {
		return (
			<Box mb={mb}>
				{showLabel && (
					<Typography component="span" className={classes.fieldLabel} style={{ minWidth: 180 }}>
						{label}:
					</Typography>
				)}
				{fieldContent}
			</Box>
		);
	}

	if (variant === 'compact') {
		return (
			<Box mb={mb}>
				{showLabel && (
					<Typography variant="subtitle2" className={classes.fieldLabel} style={{ minWidth: 180 }}>
						{label}:
					</Typography>
				)}
				<Box mt={showLabel ? 0.5 : 0}>
					{fieldContent}
				</Box>
			</Box>
		);
	}

	// Default variant
	return (
		<Box mb={mb}>
			{showLabel && (
				<Typography variant="subtitle2" gutterBottom style={{ fontWeight: 600, marginBottom: 8 }}>
					{label}:
				</Typography>
			)}
			{fieldContent}
		</Box>
	);
};

